import { useEffect, useId, useRef, useState } from "react";
import { type ReleasePr, releasesApi } from "@/api/releases";
import {
  approveBulkTasks,
  type BulkResult,
  type BulkReview,
  bulkSelectionReason,
  type BulkTask,
  bulkTaskIdentity,
  prepareBulkTasks,
} from "@/lib/bulkWorkflowOwner";
import {
  headIdentity,
  type OwnerMode,
  packageHeads,
  validOwnerReason,
} from "@/lib/workflowOwner";
export function BulkWorkflowOwnerControl(
  { tasks, all, owner, onFinished }: {
    tasks: BulkTask[];
    all: ReleasePr[];
    owner: boolean;
    onFinished: () => void;
  },
) {
  const id = useId(),
    dialog = useRef<HTMLDialogElement>(null),
    lock = useRef(false),
    live = useRef(all);
  live.current = all;
  const [open, setOpen] = useState(false),
    [frozen, setFrozen] = useState<BulkTask[]>([]),
    [reviews, setReviews] = useState<BulkReview[]>([]),
    [results, setResults] = useState<BulkResult[]>([]);
  const [mode, setMode] = useState<OwnerMode>("emergency"),
    [reason, setReason] = useState(""),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [attempted, setAttempted] = useState(false),
    [error, setError] = useState("");
  const allowed = owner && !releasesApi.workflowReadOnly;
  function current(task: BulkTask) {
    const rows = live.current.filter((pr) =>
      pr.state === "open" && pr.task?.id === task.id
    );
    return !bulkSelectionReason(rows) &&
      headIdentity(packageHeads(rows, rows[0])) === headIdentity(task.heads);
  }
  const valid = frozen.every(current);
  useEffect(() => {
    if (open) dialog.current?.showModal();
    return () => dialog.current?.close();
  }, [open]);
  useEffect(() => {
    if (!valid && !attempted) {
      setReviews([]);
      setConfirmed(false);
    }
  }, [valid, attempted]);
  function begin() {
    setFrozen(
      tasks.map((task) => ({
        ...task,
        heads: task.heads.map((head) => ({ ...head })),
      })),
    );
    setReviews([]);
    setResults([]);
    setMode("emergency");
    setReason("");
    setConfirmed(false);
    setAttempted(false);
    setError("");
    setOpen(true);
  }
  async function prepare() {
    if (lock.current || !allowed || !valid || attempted) return;
    lock.current = true;
    setBusy(true);
    setConfirmed(false);
    setError("");
    try {
      setReviews(
        await prepareBulkTasks(frozen, releasesApi.workflowPrepare, current),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "ההכנה נכשלה");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function submit() {
    if (
      lock.current || !allowed || !valid || attempted || !confirmed ||
      !validOwnerReason(reason) || reviews.length !== frozen.length ||
      reviews.some((r) => r.error || !r.prepared)
    ) return;
    lock.current = true;
    setBusy(true);
    setAttempted(true);
    setError("");
    try {
      await approveBulkTasks(reviews, { mode, reason, confirmed }, {
        storage: localStorage,
        approve: releasesApi.workflowApprove,
        current,
        progress: setResults,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "האישור לא הושלם");
    } finally {
      lock.current = false;
      setBusy(false);
      onFinished();
    }
  }
  return (
    <div className="rc-bulk">
      <button disabled={!owner || !tasks.length} onClick={begin}>
        שחרור חירום לנבחרים ({tasks.length})
      </button>
      {open && (
        <dialog
          ref={dialog}
          dir="rtl"
          aria-labelledby={id}
          onCancel={(event) => {
            event.preventDefault();
            if (!busy) setOpen(false);
          }}
          className="rc-bulk-dialog"
        >
          <h2 id={id}>אישור חבילות המשימות שנבחרו</h2>
          <p>
            אישור אחד לכל משימה, לגרסאות ולחתימות המוצגות. הביצוע נשאר כפוף לכל
            שערי השחרור.
          </p>
          {!allowed && (
            <p role="status">
              תצוגה לקריאה בלבד — אישורי בעלים אינם זמינים כאן.
            </p>
          )}
          {!valid && !attempted && (
            <p role="alert">
              אחת החבילות השתנתה או אינה מוכנה. יש לסגור, לרענן ולבחור מחדש.
            </p>
          )}
          <ul className="rc-bulk-manifests">
            {frozen.map((task) => {
              const review = reviews.find((r) => r.task.id === task.id);
              return (
                <li key={bulkTaskIdentity(task)}>
                  <h3>{task.title}</h3>
                  <ul>
                    {task.heads.map((head) => (
                      <li key={head.repo + head.number}>
                        <bdi>{head.repo} #{head.number}</bdi>
                        <code dir="ltr">{head.head_sha}</code>
                      </li>
                    ))}
                  </ul>
                  {review?.prepared && (
                    <>
                      <p>
                        {review.prepared.manifest.sql_items.length} שינויי SQL
                      </p>
                      {review.prepared.manifest.sql_items.map((sql) => (
                        <p key={sql.database + sql.path}>
                          <bdi>{sql.database} · {sql.path}</bdi>
                          <code dir="ltr">{sql.sha256}</code>
                        </p>
                      ))}
                      <code dir="ltr">{review.prepared.fingerprint}</code>
                    </>
                  )}
                  {review?.error && (
                    <p role="status">לא ניתן לאשר: {review.error}</p>
                  )}
                </li>
              );
            })}
          </ul>
          <button
            disabled={busy || !allowed || !valid || attempted}
            onClick={() => void prepare()}
          >
            בדיקת כל החבילות והכנת חתימות SQL
          </button>
          <fieldset disabled={busy || attempted || !allowed}>
            <label>
              מסלול<select
                value={mode}
                onChange={(event) => {
                  setMode(event.target.value as OwnerMode);
                  setConfirmed(false);
                }}
              >
                <option value="emergency">חירום · גם מחוץ לחלון הלילה</option>
                <option value="night">חלון לילה · 23:00–06:00</option>
              </select>
            </label>
            <p>
              {mode === "emergency"
                ? "אין המתנה לחלון הלילה. הבקר הקיים בודק את החבילות בבדיקה הקרובה, בכפוף לכל השערים."
                : "האישור ימתין לחלון הלילה ולבדיקות הבקר."}
            </p>
            <label>
              סיבה לכל המשימות<input
                value={reason}
                maxLength={2000}
                onChange={(event) => {
                  setReason(event.target.value);
                  setConfirmed(false);
                }}
              />
            </label>
            <label className="rc-bulk-confirm">
              <input
                type="checkbox"
                checked={confirmed}
                disabled={!valid || reviews.length !== frozen.length ||
                  reviews.some((r) => r.error || !r.prepared)}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              {mode === "emergency"
                ? "בדקתי את כל המשימות, גרסאות ה־PR וחתימות ה־SQL ברשימה ואני מאשר/ת חריגה מחלון הלילה לסיבה שכתבתי."
                : "בדקתי את כל המשימות, גרסאות ה־PR וחתימות ה־SQL ברשימה ואני מאשר/ת את רישום הבקשות לחלון הלילה."}
            </label>
          </fieldset>
          {results.length > 0 && (
            <ul role="status" className="rc-bulk-results">
              {results.map((result) => (
                <li key={result.task.id}>
                  <strong>
                    {result.task.title}: {result.status === "approved"
                      ? "אושר"
                      : result.status === "skipped"
                      ? "לא נשלח"
                      : result.status === "uncertain"
                      ? "דורש בירור"
                      : "סורב"}
                  </strong>
                  <p>{result.reason}</p>
                  {result.receipt?.approval_id && (
                    <bdi>קבלה: {result.receipt.approval_id}</bdi>
                  )}
                </li>
              ))}
            </ul>
          )}
          {error && <p role="alert">{error}</p>}
          <div className="rc-actions">
            <button disabled={busy} onClick={() => setOpen(false)}>
              סגירה
            </button>
            <button
              disabled={busy || attempted || !allowed || !valid || !confirmed ||
                !validOwnerReason(reason) || reviews.length !== frozen.length ||
                reviews.some((r) => r.error || !r.prepared)}
              onClick={() => void submit()}
            >
              {busy ? "ממתין…" : "רישום האישורים לנבחרים"}
            </button>
          </div>
        </dialog>
      )}
    </div>
  );
}
